import type { TError, TPostType } from '@onesource/schemas';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { ENTITY } from '../entity';

/** Query key for the "Post type" filter candidates (post types used in a space). */
export const getSpacePostTypesQueryKey = (spaceId: string) => [ENTITY, 'spaces', spaceId, 'post-types'];

interface UseGetSpacePostTypesOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch the post types actually used by posts in a space — the candidates
 * for the feed's "Post type" filter. Sourced from real posts (not the static
 * taxonomy), so the filter only offers types some visible post uses, mirroring
 * `useGetSpacePostAuthors`.
 *
 * @param spaceId - Space ID (UUID)
 * @param options - Query options (enabled, etc.) — pass `{ enabled: false }` to defer
 */
export const useGetSpacePostTypes = (spaceId: string, options?: UseGetSpacePostTypesOptions) => {
  const enabled = options?.enabled ?? true;

  return useQuery<TPostType[], TError>({
    queryKey: getSpacePostTypesQueryKey(spaceId),
    queryFn: async () => {
      const res = await axios.get<TPostType[]>(`/community-hub/spaces/${spaceId}/posts/types`);
      return res.data;
    },
    enabled: !!spaceId && enabled,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000, // 1 minute
  });
};
